import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import * as T from '../../components/Typography';
import colors from '../../theme/colors';
import Error from '../../components/Error';
import SurveyCard from '../../components/SurveyCard';
import PaymentPendingCard from '../../components/PaymentPendingCard';
import InsightCardList from '../../components/InsightCard';
import FAQSection from '../../components/FAQ';
import { useGetUserSurvey } from '../../api-calls/survey.queries';
import { useAuth } from '../../context/auth';
import * as S from './style';
import { surveyTypes } from '../../constants/data-types';
import SurveyProgress from '../../components/SurveyProgress';
import SectoralReportsCard from '../../components/SectoralDownloadCard';

const Home = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const { data, error, isLoading, isError, refetch, isRefetching } =
    useGetUserSurvey({
      salesforceContactId: user?.salesforceContactId
    });
  const needsRefetch = location.state?.needsRefetch;

  useEffect(() => {
    if (needsRefetch) {
      refetch();

      navigate(location.pathname, { replace: true });
    }
  }, [needsRefetch, refetch, navigate, location.pathname]);

  if (isLoading || isRefetching) {
    return (
      <Box
        width={'100%'}
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        data-testid="loading"
      >
        <CircularProgress color={'secondary'} />
      </Box>
    );
  }

  if (isError) {
    return <Error errorMsg={error || undefined} />;
  }

  const {
    startDate,
    endDate,
    status,
    paymentDueDate,
    paid,
    totalQuestions,
    totalAnswers,
    sectionDetails,
    sectoralReports = []
  } = data;

  return (
    <S.HomeWrapper>
      <T.H4 mt={8} color={colors.neutral.shades.shade700}>
        WELCOME TO THE EMPLOYER INDEX
      </T.H4>
      {status === surveyTypes.COMPLETED && !paid && (
        <Box mt={6}>
          <PaymentPendingCard paymentDueDate={paymentDueDate} />
        </Box>
      )}
      <Box display="flex" flexDirection="column" gap={2} mt={6}>
        <SurveyCard status={status} startDate={startDate} endDate={endDate} />
        {sectoralReports.length > 0 && (
          <SectoralReportsCard status={status} />
        )}
        {status === surveyTypes.REPORT_READY && (
          <SurveyCard status={surveyTypes.COMPLETED as 'completed'} startDate={startDate} endDate={endDate} />
        )}
      </Box>
      {(status === surveyTypes.OPEN || status === surveyTypes.IN_PROGRESS) && (
        <Box marginTop={6}>
          <SurveyProgress
            sectionDetails={sectionDetails}
            totalQuestions={totalQuestions}
            totalAnswers={totalAnswers}
          />
        </Box>
      )}
      <Box mt={4}>
        <InsightCardList />
      </Box>
      <Box mt={6} width={'100%'}>
        <T.H4 mb={4} color={colors.neutral.shades.shade700}>
          FREQUENTLY ASKED QUESTIONS
        </T.H4>
        <FAQSection surveyEndDate={endDate} />
      </Box>
    </S.HomeWrapper>
  );
};

export default Home;
